import { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { PageHeader, RiskBadge } from "@/components/common/Bits";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { ArrowLeft, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";

const SCALE = [
  { v: 1, label: "Muy bajo" }, { v: 2, label: "Bajo" }, { v: 3, label: "Moderado" },
  { v: 4, label: "Alto" }, { v: 5, label: "Crítico" },
];

function levelFromScore(s) {
  if (s < 30) return "Bajo";
  if (s < 45) return "Medio";
  if (s < 60) return "Medio-Alto";
  if (s < 80) return "Alto";
  return "Crítico";
}

export default function AssessmentRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const reevalId = params.get("reeval");
  const [catalogs, setCatalogs] = useState(null);
  const [ini, setIni] = useState(null);
  const [answers, setAnswers] = useState({});
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/catalogs").then(({ data }) => {
      setCatalogs(data);
      setAnswers(Object.fromEntries(data.dimensions.map((d) => [d.key, 2])));
    });
    api.get(`/initiatives/${id}`).then(({ data }) => setIni(data.initiative));
  }, [id]);

  const preview = useMemo(() => {
    const vals = Object.values(answers);
    if (!vals.length) return null;
    const inherent = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 20);
    return { inherent, level: levelFromScore(inherent) };
  }, [answers]);

  const submit = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/initiatives/${id}/assessments`, { answers, notes, reevaluation_id: reevalId });
      toast.success(`Evaluación v${data.version} registrada · riesgo ${data.inherent_level} / residual ${data.residual_level}`);
      navigate(`/iniciativas/${id}`);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!catalogs || !ini) return null;

  return (
    <div className="max-w-4xl mx-auto">
      <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="text-muted-foreground -ml-2 mb-4">
        <ArrowLeft className="h-4 w-4 mr-1" /> Volver
      </Button>
      <PageHeader
        title={`Risk Assessment · ${ini.name}`}
        subtitle={reevalId ? "Reevaluación en curso: se generará una nueva versión conservando la anterior." : "Cuestionario de 15 dimensiones. El sistema calcula riesgo inherente, residual, controles obligatorios y ruta de aprobación."}
        testId="assessment-header"
      />

      <div className="grid lg:grid-cols-3 gap-6 items-start">
        <div className="lg:col-span-2 space-y-3">
          {catalogs.dimensions.map((dim, idx) => (
            <Card key={dim.key} className="shadow-card border-border">
              <CardContent className="p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{idx + 1}. {dim.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{dim.question}</p>
                  </div>
                </div>
                <div className="grid grid-cols-5 gap-1.5 mt-3" data-testid={`dimension-${dim.key}`}>
                  {SCALE.map((s) => (
                    <button
                      key={s.v}
                      onClick={() => setAnswers((p) => ({ ...p, [dim.key]: s.v }))}
                      data-testid={`dim-${dim.key}-${s.v}`}
                      className={cn(
                        "rounded-lg border px-1 py-2 text-[11px] font-medium transition-colors",
                        answers[dim.key] === s.v
                          ? s.v >= 4 ? "border-risk-high bg-risk-high/10 text-risk-high" : s.v === 3 ? "border-risk-medium bg-risk-medium/10 text-risk-medium" : "border-accent bg-accent/10 text-accent"
                          : "border-border bg-card text-muted-foreground hover:border-primary/40"
                      )}
                    >
                      {s.v} · {s.label}
                    </button>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
          <Card className="shadow-card border-border">
            <CardContent className="p-5 space-y-2">
              <Label className="text-xs font-medium text-muted-foreground">Notas del evaluador</Label>
              <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Observaciones, supuestos y evidencias consideradas" data-testid="assessment-notes" />
            </CardContent>
          </Card>
        </div>

        <div className="lg:sticky lg:top-24">
          <Card className="shadow-card border-border">
            <CardContent className="p-5">
              <div className="flex items-center gap-2 mb-4">
                <div className="h-8 w-8 rounded-lg bg-primary/8 text-primary flex items-center justify-center"><ShieldCheck className="h-4 w-4" /></div>
                <p className="font-display font-semibold text-sm">Resultado preliminar</p>
              </div>
              {preview && (
                <>
                  <div className="flex items-end justify-between">
                    <span className="font-display text-4xl font-semibold tabular">{preview.inherent}</span>
                    <RiskBadge level={preview.level} />
                  </div>
                  <Progress value={preview.inherent} className="h-2 mt-3" />
                  <p className="text-[11px] text-muted-foreground mt-3 leading-relaxed">
                    El riesgo residual, los controles obligatorios, la ruta de aprobación y las revisiones (DPO, clínica, seguridad, Comité) se calculan en el servidor al registrar la evaluación.
                  </p>
                </>
              )}
              <Button onClick={submit} disabled={saving} className="w-full mt-5 bg-accent hover:bg-accent/90 text-accent-foreground" data-testid="assessment-submit-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Registrar evaluación"}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
